"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  Wallet,
  Shield,
  Clock,
  CheckCircle,
  Copy,
  CreditCard,
  QrCode,
  Landmark,
  X,
  ShieldCheck,
  Check,
  Zap,
} from "lucide-react";
import { CrossmintEmbeddedCheckout } from "@crossmint/client-sdk-react-ui";
import * as ethers from "ethers";
import toast, { Toaster } from "react-hot-toast";
import { getBalance, createInvoice as createPaymentInvoice, refreshCoinbaseOrderStatus } from "../services/authapi";
import { useNavigate, useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import { SiCashapp } from "react-icons/si";
import { isBetaUser } from "../betaConfig";
import { peerExtensionSdk } from "@zkp2p/sdk";
import {
  AlertTriangle,
  RefreshCw,
  Globe,
  Download,
  ShieldAlert,
} from "lucide-react";
import { isEmailDisabledFor } from "../config/disabledEmailRecipients";
import { CoinbaseHeadlessOnrampModal } from "./CoinbaseHeadlessOnrampModal";

interface PaymentMethod {
  id: string;
  name: string;
  icon: React.ReactNode;
  fee: string;
  disabled?: boolean;
  comingSoon?: boolean;
  mobileDisabled?: boolean;
}

export const ALL_PAYMENT_METHOD_IDS = [
  "coinbase-onramp",
  "coinbase-headless", // Beta: Headless Onramp with Apple Pay / Google Pay
  "card-bank",
  "lightning-btc",
  // `lightning-cashapp` is a UI-only synonym for `lightning-btc` — same
  // backend (FixedFloat / SimpleSwap Lightning), same /lightning create
  // route — that we surface as a second tile so buyers who recognise
  // "CashApp Pay" find it (the "Add Money via Lightning" tile speaks to
  // Bitcoin-natives; the "CashApp Pay" tile speaks to consumer buyers).
  // The submit handler treats both ids identically.
  "lightning-cashapp",
  "cashapp",
  "wallet",
  // PYUSD (PayPal / Venmo) — beta only. Both ids route to the same
  // SimpleSwap-backed flow; the source only changes branded copy.
  "paypal",
  "venmo",
] as const;

export type PaymentMethodId = (typeof ALL_PAYMENT_METHOD_IDS)[number];

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  "card-bank": "Card / Bank",
  "coinbase-onramp": "Coinbase",
  "coinbase-headless": "Coinbase Pay (Apple Pay / Google Pay)",
  "lightning-btc": "Add Money via Lightning",
  "lightning-cashapp": "CashApp Pay",
  cashapp: "Wallet Apps (CashApp, Venmo, etc.)",
  wallet: "Wallet Transfer",
  paypal: "PayPal (PYUSD)",
  venmo: "Venmo (PYUSD)",
};

// Payment methods temporarily hidden from selection everywhere in the main
// app (Add Funds + Collect link picker, previews, and seller toggles).
// `cashapp` ("Wallet Apps") is the only method powered by the zkP2P peer
// extension — it's hidden while the peer extension is down and pending a
// re-implementation. To restore it, just remove the id from this array.
// (The hosted checkout + Developer API settings hide their own `peer` id
// separately, since `cashapp` means CashApp-via-Lightning over there.)
export const HIDDEN_PAYMENT_METHOD_IDS: readonly string[] = ["cashapp"];

interface PaymentFlowProps {
  targetWalletAddress: string;
  payerEmail?: string | null;
  recipientName?: string;
  recipientPhotoURL?: string;
  title?: string;
  subtitle?: string;
  presetAmounts?: number[];
  fixedAmount?: number;
  minAmount?: number;
  maxAmount?: number;
  amountPlaceholder?: string;
  isPublic?: boolean;
  showWalletDisplay?: boolean;
  showManagePayments?: boolean;
  // Compact mode renders the whole flow as a single elegant card with no
  // per-section chrome and trims the verbose extras (recipient header,
  // security checklist, About CashTap). The parent supplies the outer card.
  compact?: boolean;
  buttonText?: string;
  accentColor?: string;
  theme?: "dark" | "light";
  onSuccess?: () => void;
  disabledPaymentMethods?: string[];
  // When set, public-mode submissions will create a payment invoice on the
  // backend, claim a dedicated wallet from the pool, and route the chosen
  // payment method to that dedicated wallet so the seller gets full
  // attribution (payer email + method + source) in their history.
  invoiceSource?: "request" | "donation" | "buymeCoffee" | "manual";
  invoiceCollectSlug?: string;
  sellerUserId?: string;
}

export function PaymentFlow({
  targetWalletAddress,
  payerEmail,
  recipientName,
  recipientPhotoURL,
  title = "Add Funds",
  subtitle = "Top up your wallet with USD on Base using your preferred payment method",
  presetAmounts,
  fixedAmount,
  minAmount,
  maxAmount,
  amountPlaceholder,
  isPublic = false,
  showWalletDisplay = true,
  showManagePayments = true,
  compact = false,
  buttonText = "Continue to Payment",
  accentColor,
  theme = "dark",
  onSuccess,
  disabledPaymentMethods = [],
  invoiceSource,
  invoiceCollectSlug,
  sellerUserId,
}: PaymentFlowProps) {
  const isLight = theme === "light";
  const cardBg = isLight
    ? "bg-white border border-gray-200 shadow-sm"
    : "bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 backdrop-blur-sm";
  const headingText = isLight ? "text-gray-900" : "text-white";
  const bodyText = isLight ? "text-gray-600" : "text-gray-400";
  const mutedText = isLight ? "text-gray-500" : "text-gray-500";
  const inputBg = isLight
    ? "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20"
    : "bg-gray-800/30 border-gray-700/50 text-white placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20";
  const dividerBorder = isLight ? "border-gray-200" : "border-gray-700/50";
  // In compact mode the parent owns the card, so each section drops its own
  // chrome and headings shrink — keeping the whole flow on one elegant card.
  const sectionCard = compact
    ? ""
    : `${cardBg} rounded-3xl p-5 sm:p-6 md:p-8`;
  const sectionHeading = compact
    ? `text-xs sm:text-sm font-semibold ${headingText} mb-1.5 sm:mb-2`
    : `text-lg sm:text-xl font-bold ${headingText} mb-4`;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedMethod, setSelectedMethod] =
    useState<string>("coinbase-onramp");
  const [amount, setAmount] = useState<string>(
    fixedAmount ? fixedAmount.toString() : "",
  );
  const [userWallet, setUserWallet] = useState<string>("");
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [loading, setLoading] = useState<boolean>(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [disclaimerAgreed, setDisclaimerAgreed] = useState<boolean>(false);

  const [isWalletModalOpen, setIsWalletModalOpen] = useState<boolean>(false);
  const [isStripeOnrampOpen, setIsStripeOnrampOpen] = useState<boolean>(false);
  const [isCrossmintModalOpen, setIsCrossmintModalOpen] =
    useState<boolean>(false);
  const [isPeerSuccessModalOpen, setIsPeerSuccessModalOpen] =
    useState<boolean>(false);

  const [stripeClientSecret, setStripeClientSecret] = useState<string>("");
  const [crossmintOrderId, setCrossmintOrderId] = useState<string | null>(null);
  const [crossmintClientSecret, setCrossmintClientSecret] = useState<
    string | null
  >(null);

  const [isCoinbaseHeadlessOpen, setIsCoinbaseHeadlessOpen] =
    useState<boolean>(false);

  const [isMobile, setIsMobile] = useState<boolean>(false);

  const [showExtensionDisclaimer, setShowExtensionDisclaimer] =
    useState<boolean>(false);
  const [peerExtensionAvailable, setPeerExtensionAvailable] =
    useState<boolean>(false);
  const [peerExtensionState, setPeerExtensionState] = useState<
    "unknown" | "needs_install" | "needs_connection" | "ready"
  >("unknown");
  const [peerProcessing, setPeerProcessing] = useState<boolean>(false);

  const REFERRER = "Cashtap";
  const REFERRER_LOGO = "https://cashtap.cash/logo.png";
  const CALLBACK_URL = "https://cashtap.cash/add-funds?peerSuccess=true";
  const BASE_USDC_TOKEN = "8453:0x833589fcd6edb6e08f4c7c32d4f71b54bda02913";
  const EXTENSION_INFO_PATH = "/extension-info";

  // In public mode (collect pages, public invoice page) we ask the buyer
  // for their email up front and use it as the payer attribution. In-app
  // flows fall back to the prop passed in.
  const [buyerEmail, setBuyerEmail] = useState<string>("");
  const usesInvoiceFlow = isPublic && !!invoiceSource;
  // Per-seller opt-out for the buyer-email prompt. When disabled, the email
  // field is hidden and validation is skipped — the invoice is created with
  // an empty buyer email and no buyer-side receipt is sent.
  const emailDisabled = isEmailDisabledFor({
    wallet: targetWalletAddress,
    username: recipientName,
  });
  const requiresBuyerEmail = usesInvoiceFlow && !emailDisabled;
  const resolvedEmail =
    payerEmail || (usesInvoiceFlow ? buyerEmail.trim() : undefined);

  // Holds the dedicated wallet address claimed for the in-flight payment.
  // Each method downstream uses this in place of the seller's main wallet
  // so the webhook can attribute incoming funds to a specific invoice.
  const [activeDedicatedAddress, setActiveDedicatedAddress] =
    useState<string>("");
  const [activeInvoiceId, setActiveInvoiceId] = useState<string>("");

  /**
   * Create a payment invoice on the backend and claim a dedicated wallet
   * for the buyer's deposit. Returns the dedicated address. Throws on
   * validation failures or pool exhaustion.
   */
  const generateInvoiceForPayment = async (
    parsedAmount: number,
    methodId: string,
  ): Promise<string> => {
    if (!invoiceSource) {
      throw new Error("Invoice flow not configured for this page.");
    }
    if (requiresBuyerEmail && !buyerEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      throw new Error("Please enter a valid email address.");
    }
    if (!targetWalletAddress) {
      throw new Error("Recipient wallet missing.");
    }
    const inv = await createPaymentInvoice({
      buyerEmail: buyerEmail.trim(),
      amount: parsedAmount,
      sellerWallet: targetWalletAddress,
      sellerUserId,
      sellerUsername: recipientName,
      source: invoiceSource,
      paymentMethod: methodId,
      collectSlug: invoiceCollectSlug,
    });
    setActiveDedicatedAddress(inv.dedicatedWalletAddress);
    setActiveInvoiceId(inv.invoiceId);
    return inv.dedicatedWalletAddress;
  };

  const paymentMethods: PaymentMethod[] = [
    {
      id: "coinbase-onramp",
      name: "Coinbase",
      icon: (
        <div className="relative w-full overflow-hidden rounded-2xl transition-all duration-300 group">
          <div className={`flex flex-col ${compact ? "gap-2.5" : "gap-4"}`}>
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Card-network + wallet logos, each in its own white tile */}
              <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
                {[
                  { src: "/Visa-logo.png", alt: "Visa" },
                  { src: "/mastercard-logo.png", alt: "Mastercard" },
                  { src: "/applepay-logo.png", alt: "Apple Pay" },
                  { src: "/googlepay-logo.png", alt: "Google Pay" },
                ].map((logo) => (
                  <div
                    key={logo.alt}
                    className={`flex items-center justify-center rounded-lg sm:rounded-xl ${compact ? "px-1.5 py-1" : "px-2 py-1.5"} ${isLight ? "bg-white border border-gray-200 shadow-sm" : "bg-white shadow-sm"}`}
                  >
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className={`${compact ? "h-4 sm:h-6" : "h-5 sm:h-7"} w-auto object-contain`}
                    />
                  </div>
                ))}
              </div>

              <div className="flex-1 min-w-0">
                {/* Heading is hidden on mobile per design — logos + subtitle carry it there */}
                <h4
                  className={`hidden sm:block ${compact ? "text-[13px] sm:text-base" : "text-base sm:text-lg"} font-bold ${isLight ? "text-gray-900" : "text-white"} tracking-tight`}
                >
                  <span className="text-[#61CE70]">Pay</span> with Visa /
                  MasterCard, Apple Pay or Google Pay
                </h4>
                <p
                  className={`${compact ? "text-xs" : "text-sm"} font-medium ${isLight ? "text-gray-500" : "text-gray-400"} mt-0.5`}
                >
                  Instant and secure payment
                </p>
              </div>
            </div>

            <div
              className={`${compact ? "hidden sm:flex" : "flex"} items-center gap-3 ${compact ? "pt-2" : "pt-3"} border-t ${isLight ? "border-gray-200" : "border-gray-800/60"}`}
            >
              <div
                className={`flex items-center gap-1.5 ${compact ? "px-2.5 py-1" : "px-3 py-1.5"} rounded-full font-semibold text-xs ${isLight ? "bg-white text-gray-900 border border-gray-200 shadow-sm" : "bg-gray-900/40 text-white border border-gray-700/50"}`}
              >
                <Zap
                  size={14}
                  className="text-[#61CE70]"
                  fill="currentColor"
                  strokeWidth={0}
                />
                <span>Instant</span>
              </div>
              <div
                className={`flex items-center gap-1.5 font-semibold text-xs ${isLight ? "text-indigo-600" : "text-indigo-300"}`}
              >
                <ShieldCheck size={15} strokeWidth={2.5} />
                <span>Secure Checkout</span>
              </div>
            </div>
          </div>
        </div>
      ),
      fee: "Variable (~1-3%)",
    },
    {
      id: "card-bank",
      name: "Card / Bank",
      icon: (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
            <div>
              <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                <CreditCard
                  size={28}
                  className={`${isLight ? "text-indigo-600" : "text-indigo-400"} shrink-0`}
                  strokeWidth={1.8}
                />
                <span
                  className={`font-medium text-base ${isLight ? "text-gray-900" : "text-white"} whitespace-nowrap`}
                >
                  Card
                </span>
                <span
                  className={`hidden sm:inline ${isLight ? "text-gray-400" : "text-gray-500"}`}
                >
                  /
                </span>
                <Landmark
                  size={28}
                  className={`${isLight ? "text-indigo-600" : "text-indigo-400"} shrink-0`}
                  strokeWidth={1.8}
                />
                <span
                  className={`font-medium text-base ${isLight ? "text-gray-900" : "text-white"} whitespace-nowrap`}
                >
                  Bank
                </span>
              </div>

              <div className="mt-2 relative group/sc">
                <button
                  type="button"
                  className={`text-sm underline underline-offset-2 decoration-dotted cursor-pointer ${isLight ? "text-indigo-600 hover:text-indigo-700" : "text-indigo-400 hover:text-indigo-300"} transition-colors`}
                >
                  Supported Countries
                </button>
                <div className="absolute left-0 top-full mt-2 z-50 w-[340px] opacity-0 invisible group-hover/sc:opacity-100 group-hover/sc:visible transition-all duration-200">
                  <div
                    className={`rounded-xl p-4 text-xs leading-relaxed shadow-xl border ${isLight ? "bg-white border-gray-200 text-gray-700" : "bg-[#1a1f2e] border-gray-700 text-gray-300"}`}
                  >
                    <p className="font-semibold mb-2 text-sm">
                      Available Regions
                    </p>
                    <p className="mb-2">
                      <span className="font-medium">United States</span>{" "}
                      (excluding Hawaii) — USD
                    </p>
                    <p className="mb-1">
                      <span className="font-medium">Europe</span> (EUR):
                    </p>
                    <p className="mb-2 text-[11px]">
                      Austria, Belgium, Bulgaria, Croatia, Cyprus, Czech
                      Republic, Denmark, Estonia, Finland, France, Germany,
                      Greece, Ireland, Italy, Latvia, Lithuania, Luxembourg,
                      Malta, Netherlands, Norway, Poland, Portugal, Romania,
                      Slovakia, Slovenia, Spain, Sweden
                    </p>
                    <p className="font-semibold mb-1 text-sm">
                      Payment Methods
                    </p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Debit cards</li>
                      <li>Credit cards</li>
                      <li>Bank account payments (US only)</li>
                    </ul>
                    <p
                      className={`mt-2 text-[10px] ${isLight ? "text-gray-400" : "text-gray-500"}`}
                    >
                      You must be a resident and physically located in a
                      supported country.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 sm:mt-0">
              <img
                src="/Visa-logo.png"
                alt="Visa"
                className={`h-5 sm:h-8 md:h-10 bg-white rounded-lg w-auto object-contain ${isLight ? "shadow-md border border-gray-100" : "opacity-90"}`}
              />
              <img
                src="/mastercard-logo.png"
                alt="Mastercard"
                className={`h-5 sm:h-8 md:h-10 rounded-lg w-auto object-contain ${isLight ? "shadow-md" : "backdrop-invert opacity-90"}`}
              />
              <img
                src="/amex.png"
                alt="Amex"
                className={`h-6 sm:h-9 md:h-12 scale-110 sm:scale-125 md:scale-150 rounded-lg w-auto object-contain ${isLight ? "shadow-md" : "opacity-90"}`}
              />
              <img
                src="/discover.jpg"
                alt="Discover"
                className={`h-5 sm:h-8 md:h-10 rounded-lg w-auto object-contain ${isLight ? "shadow-md border border-gray-100" : "brightness-150 opacity-90"}`}
              />
            </div>
          </div>
          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5 pt-3 border-t ${isLight ? "border-gray-200" : "border-gray-800/60"}`}
          >
            <div className="flex items-center gap-4 text-sm">
              <div
                className={`flex items-center gap-1.5 ${isLight ? "bg-green-50 text-green-600 border border-green-200" : "bg-green-900/40 text-green-400"} px-2.5 py-1 rounded-full font-medium`}
              >
                <Check size={14} strokeWidth={3} />
                <span>Instant</span>
              </div>
              <div
                className={`flex items-center gap-1.5 ${isLight ? "text-indigo-600" : "text-indigo-300"} font-medium`}
              >
                <ShieldCheck size={14} strokeWidth={2.5} />
                <span>Secure Checkout</span>
              </div>
            </div>

            <div
              className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-xs font-medium ${isLight ? "bg-gray-50 text-gray-700 border border-gray-200 shadow-sm" : "bg-white text-black"}`}
            >
              <span>Powered by</span>
              <div className="flex justify-center items-center">
                <img
                  src="/Stripe_Logo.png"
                  alt="Stripe"
                  className="h-5 w-auto object-contain rounded-full opacity-90"
                />
              </div>
            </div>
          </div>
        </div>
      ),
      fee: "0",
    },

    {
      // Sibling tile to `lightning-btc` — same backend (Lightning rail
      // via FF / SimpleSwap, same /lightning page), different framing
      // for buyers who recognise "CashApp Pay" without thinking about
      // the underlying protocol. The submit handler routes both ids
      // through the same navigate() branch. Rendered ABOVE the
      // "Add Money via Lightning" tile because the CashApp brand is
      // more recognisable to most buyers, so it gets first-glance
      // priority in the picker.
      id: "lightning-cashapp",
      name: "CashApp Pay (Instant via Lightning)",
      icon: (
        <div className="flex items-center gap-3 sm:gap-4 w-full">
          <div className={`${compact ? "w-8 h-8 sm:w-10 sm:h-10" : "w-9 h-9 sm:w-10 sm:h-10"} rounded-lg bg-[#00D632]/12 border border-[#00D632]/20 flex items-center justify-center shrink-0`}>
            <SiCashapp className="w-6 h-6 sm:w-9 sm:h-9 rounded-lg bg-white text-[#00D632]" />
          </div>
          <div className="min-w-0">
            <div
              className={`${compact ? "text-sm sm:text-base" : "text-base"} font-semibold ${isLight ? "text-green-600" : "text-green-400"} truncate`}
            >
              CashApp Pay
            </div>
            <span
              className={`${compact ? "hidden sm:inline" : ""} text-xs sm:text-sm ${isLight ? "text-gray-500" : "text-gray-400"}`}
            >
              Instant payment via Lightning
            </span>
          </div>
        </div>
      ),
      fee: "0",
    },

    {
      id: "lightning-btc",
      // Method name doubles as the aria-label / tile heading the parent
      // tile-list uses for accessibility; the rich `icon` block below is
      // what's actually rendered. Lead with what the buyer DOES ("Add
      // Money via Lightning") rather than the rail brand, matching the
      // labelling decision we made for the Coinbase tile.
      name: "Add Money via Lightning",
      icon: (
        <div className="flex items-center gap-4 sm:gap-5 w-full">
          {/* Concentric-ring lightning icon — mirrors the marketing
              tile shipped with the design ask:
                • outermost dashed ring (soft pulse, signals "live")
                • inner dashed ring (denser, no motion)
                • solid green-gradient core with a white Zap glyph and
                  a green glow shadow so it reads as the focal point
              All decorative — no aria-hidden needed because the parent
              tile already exposes the label/sub via name + text below. */}
          <div className={`relative ${compact ? "w-12 h-12 sm:w-20 sm:h-20" : "w-16 h-16 sm:w-20 sm:h-20"} shrink-0 flex items-center justify-center`}>
            <span className="absolute inset-0 rounded-full border border-dashed border-[#61CE70]/30 animate-[ping_2.6s_cubic-bezier(0,0,0.2,1)_infinite]" />
            <span className="absolute inset-2 rounded-full border border-dashed border-[#61CE70]/55" />
            <span
              className={`relative ${compact ? "w-8 h-8 sm:w-12 sm:h-12" : "w-10 h-10 sm:w-12 sm:h-12"} rounded-full flex items-center justify-center`}
              style={{
                background: "linear-gradient(135deg, #61CE70 0%, #3FA653 100%)",
                boxShadow:
                  "0 0 22px rgba(97,206,112,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              <Zap
                className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                strokeWidth={2.6}
                fill="white"
              />
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <h3
              className={`${compact ? "text-[13px] sm:text-lg" : "text-base sm:text-lg"} font-bold tracking-tight leading-tight ${
                isLight ? "text-gray-900" : "text-white"
              }`}
            >
              Add Money{" "}
              <span className="text-[#61CE70]">via Lightning</span>
            </h3>
            <p
              className={`${compact ? "hidden sm:block" : "block"} mt-1 text-xs sm:text-sm ${
                isLight ? "text-gray-500" : "text-gray-400"
              }`}
            >
              Instant Payment using Lightning Invoice
            </p>
          </div>
        </div>
      ),
      fee: "0",
    },

    {
      id: "cashapp",
      name: "Wallet Apps (Via Your Wallet)",
      icon: (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 w-full rounded-xl shadow-sm">
          <div className="min-w-0 flex-1">
            <h3
              className={`font-semibold ${isLight ? "text-green-600" : "text-green-400"} ${compact ? "text-[13px] sm:text-lg" : "text-base sm:text-lg"} tracking-tight`}
            >
              Add Money via Wallet Apps
            </h3>

            <p
              className={`${compact ? "hidden sm:block" : "block"} mt-1 text-xs sm:text-sm ${isLight ? "text-gray-500" : "text-gray-400"}`}
            >
              Fast & secure deposits with popular apps
            </p>

            <div className={`flex flex-wrap ${compact ? "gap-2 mt-2.5" : "gap-3 mt-4"}`}>
              <div className={`${compact ? "w-8 h-8 sm:w-10 sm:h-10" : "w-14 h-14 sm:w-16 sm:h-16"} rounded-full overflow-hidden shadow-md transition-transform hover:scale-110 active:scale-105 bg-white`}>
                <SiCashapp className="w-full h-full rounded-full text-[#00D632]" />
              </div>
              <div className={`${compact ? "w-8 h-8 sm:w-10 sm:h-10 border-2" : "w-14 h-14 sm:w-16 sm:h-16 border-[4px]"} rounded-full overflow-hidden shadow-md border-green-400 transition-transform hover:scale-110 active:scale-105 bg-white p-1`}>
                <img
                  src="/chime-logo.svg"
                  alt="Chime"
                  className="w-full h-full object-contain"
                />
              </div>

              <div className={`${compact ? "w-8 h-8 sm:w-10 sm:h-10" : "w-14 h-14 sm:w-16 sm:h-16"} rounded-full overflow-hidden shadow-md transition-transform hover:scale-110 active:scale-105 bg-white p-1`}>
                <img
                  src="/venmo.png"
                  alt="Venmo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className={`${compact ? "w-8 h-8 sm:w-10 sm:h-10" : "w-14 h-14 sm:w-16 sm:h-16"} rounded-full overflow-hidden shadow-md transition-transform hover:scale-110 active:scale-105 bg-white p-1.5`}>
                <img
                  src="/zelle.png"
                  alt="Zelle"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className={`${compact ? "w-8 h-8 sm:w-10 sm:h-10" : "w-14 h-14 sm:w-16 sm:h-16"} rounded-full overflow-hidden shadow-md transition-transform hover:scale-110 active:scale-105 bg-white p-1.5`}>
                <img
                  src="/revoult.png"
                  alt="Revolut"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className={`${compact ? "w-8 h-8 sm:w-10 sm:h-10" : "w-14 h-14 sm:w-16 sm:h-16"} rounded-full overflow-hidden shadow-md transition-transform hover:scale-110 active:scale-105 bg-white/90 p-1.5`}>
                <img
                  src="/wise.png"
                  alt="Wise"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      ),
      fee: "0",
      mobileDisabled: true,
    },
    {
      id: "wallet",
      name: "Transfer from Wallet",
      icon: (
        <div className="flex items-center gap-3 sm:gap-4 w-full">
          <div className={`${compact ? "p-2 sm:p-2.5" : "p-2.5"} bg-[#61CE70]/10 rounded-xl shrink-0`}>
            <Wallet
              className={`${compact ? "w-5 h-5 sm:w-7 sm:h-7" : "w-6 h-6 sm:w-7 sm:h-7"} text-[#61CE70]`}
              strokeWidth={2}
            />
          </div>
          <div className="min-w-0">
            <div
              className={`${compact ? "text-sm sm:text-base" : "text-base"} font-semibold ${isLight ? "text-green-600" : "text-[#81E68A]"} truncate`}
            >
              Wallet Transfer
            </div>
            <span
              className={`${compact ? "hidden sm:inline" : ""} text-xs sm:text-sm ${isLight ? "text-gray-500" : "text-gray-400"}`}
            >
              Transfer from your wallet
            </span>
          </div>
        </div>
      ),
      fee: "0",
    },
    // ─── Beta-only payment methods: PayPal and Venmo (PYUSD). Same visual
    //     structure — small square brand-coloured container on the left,
    //     title + subtitle on the right, Beta pill inline with the title.
    //     (Coinbase's Headless Onramp — Apple Pay / Google Pay — is now live
    //     for everyone and folded into the "coinbase-onramp" tile above.)
    ...(!isPublic && isBetaUser(payerEmail)
      ? [
          {
            id: "paypal",
            name: "Pay with PayPal",
            icon: (
              <div className="flex items-center gap-3 sm:gap-4 w-full">
                {/* Brand pill: fixed square so both PayPal and Venmo tiles
                have identical left-column footprints regardless of how
                wide the underlying logo asset is. Subtle PayPal-blue
                ring tells the eye it's a PayPal tile even before
                reading the title. */}
                <div
                  className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-white ring-1 ring-[#0070BA]/30 shadow-sm flex items-center justify-center shrink-0 overflow-hidden`}
                >
                  <img
                    src="/paypal-short.svg"
                    alt=""
                    aria-hidden="true"
                    className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-base font-semibold ${isLight ? "text-gray-900" : "text-white"} truncate`}
                    >
                      Pay with PayPal
                    </span>
                    <span className="text-[9px] uppercase tracking-[0.08em] bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full font-bold leading-none shrink-0">
                      Beta
                    </span>
                  </div>
                  <p
                    className={`text-xs sm:text-sm ${isLight ? "text-gray-500" : "text-gray-400"} mt-0.5 truncate`}
                  >
                    Instant from your PayPal balance
                  </p>
                </div>
              </div>
            ),
            fee: "0",
          },
          {
            id: "venmo",
            name: "Pay with Venmo",
            icon: (
              <div className="flex items-center gap-3 sm:gap-4 w-full">
                {/* Same square as PayPal — identical dimensions so the two
                tiles align perfectly when stacked. Venmo's accent ring
                uses Venmo blue so the eye can distinguish them at a
                glance without reading. */}
                <div
                  className={`w-11 h-11 sm:w-12 sm:h-12 bg-[#3396CD] py-1 rounded-xl`}
                >
                  <img
                    src="/venmo.png"
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full md:w-full sm:h-10"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-base font-semibold ${isLight ? "text-gray-900" : "text-white"} truncate`}
                    >
                      Pay with Venmo
                    </span>
                    <span className="text-[9px] uppercase tracking-[0.08em] bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full font-bold leading-none shrink-0">
                      Beta
                    </span>
                  </div>
                  <p
                    className={`text-xs sm:text-sm ${isLight ? "text-gray-500" : "text-gray-400"} mt-0.5 truncate`}
                  >
                    Instant from your Venmo balance
                  </p>
                </div>
              </div>
            ),
            fee: "0",
          },
        ]
      : []),
    // Mobile Wallets (Apple Pay / Google Pay) tile removed —
    // surfacing a coming-soon tile that the buyer can't actually use
    // adds friction to the picker without delivering value. Re-add
    // this entry when the Stripe-OnRamp-backed flow is production-ready;
    // the `mobile-wallets` submit branch and StripeOnrampModal further
    // down stay in place so wiring it back is a one-tile change.
  ];

  useEffect(() => {
    if (
      searchParams.get("peerSuccess") === "true" ||
      searchParams.get("cbSuccess") === "true"
    ) {
      setIsPeerSuccessModalOpen(true);
      if (!isPublic) {
        navigate("/wallet", { replace: true });
      }
    }
  }, [searchParams, navigate, isPublic]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        ),
      );
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (targetWalletAddress && ethers.isAddress(targetWalletAddress)) {
      setUserWallet(targetWalletAddress);
    }
  }, [targetWalletAddress]);

  useEffect(() => {
    if (!userWallet) return;

    QRCode.toDataURL(userWallet, {
      width: 300,
      margin: 2,
      color: { dark: "#61CE70", light: "#ffffff" },
    })
      .then((url) => setQrCodeUrl(url))
      .catch((err) => {
        console.error("Failed to generate QR code:", err);
        toast.error("Failed to generate QR code");
      });
  }, [userWallet]);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!userWallet || !ethers.isAddress(userWallet)) return;

      setLoading(true);
      try {
        const balance = await getBalance(userWallet);
        setWalletBalance(balance);
      } catch (err: any) {
        console.error("Error fetching balance:", err);
        toast.error("Failed to fetch wallet balance.");
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [userWallet]);

  useEffect(() => {
    const checkExtension = async () => {
      try {
        // Give the extension time to initialize
        await new Promise((resolve) => setTimeout(resolve, 500));

        const available = peerExtensionSdk.isAvailable();
        console.log("Peer extension available:", available);
        setPeerExtensionAvailable(available);

        if (!available) {
          setPeerExtensionState("needs_install");
          return;
        }

        const state = await peerExtensionSdk.getState();
        console.log("Peer extension state:", state);
        setPeerExtensionState(state);
      } catch (error) {
        console.error("Error checking peer extension:", error);
        setPeerExtensionState("needs_install");
      }
    };

    checkExtension();
  }, []);

  const startCashAppPayment = useCallback(
    async (depositAddressOverride?: string) => {
      const target = depositAddressOverride || userWallet;
      if (!target) {
        toast.error("Wallet not connected");
        return;
      }

      const parsed = parseFloat(amount);
      const effectiveMin = minAmount ?? 0.5;
      const effectiveMax = maxAmount ?? 10000;
      if (isNaN(parsed) || parsed < effectiveMin || parsed > effectiveMax) {
        toast.error(
          `Amount must be between $${effectiveMin} and $${effectiveMax.toLocaleString()}`,
        );
        return;
      }

      setPeerProcessing(true);

      try {
        if (
          !peerExtensionAvailable ||
          peerExtensionState === "needs_install"
        ) {
          toast("Redirecting to extension installation guide...", {
            duration: 4000,
          });
          navigate(EXTENSION_INFO_PATH);
          return;
        }

        let state = peerExtensionState as any;

        if (state === "needs_connection") {
          const approved = await peerExtensionSdk.requestConnection();
          if (!approved) {
            toast.error("Connection to Cashtap extension not approved");
            return;
          }
          state = await peerExtensionSdk.getState();
          setPeerExtensionState(state);
        }

        if (state !== "ready") {
          toast.error(
            "Cashtap extension not ready. Please install or enable it.",
          );
          return;
        }

        // Pass the dedicated wallet (or the user's wallet for in-app
        // flows) as the recipient. The peer extension handles the on-ramp
        // and ultimately sends USDC on Base to this address.
        peerExtensionSdk.onramp({
          referrer: REFERRER,
          referrerLogo: REFERRER_LOGO,
          callbackUrl: CALLBACK_URL,
          inputCurrency: "USD",
          inputAmount: parsed.toString(),
          toToken: BASE_USDC_TOKEN,
          recipient: target,
        } as any);
      } catch (err: any) {
        console.error(err);
        toast.error(
          err.message ||
            "Failed to initiate CashApp payment. Please try again.",
        );
      } finally {
        setPeerProcessing(false);
      }
    },
    [
      userWallet,
      amount,
      peerExtensionAvailable,
      peerExtensionState,
      minAmount,
      maxAmount,
      navigate,
      REFERRER,
      REFERRER_LOGO,
      CALLBACK_URL,
      BASE_USDC_TOKEN,
      EXTENSION_INFO_PATH,
    ],
  );

  const proceedWithCashAppAfterDisclaimer = (depositAddress?: string) => {
    startCashAppPayment(depositAddress);
  };

  // Handler for Coinbase Headless Onramp modal submission
  const handleCoinbaseHeadlessSubmit = async (data: {
    country: string;
    countryCode: string;
    email: string;
    phoneNumber: string;
    paymentMethod: string;
  }) => {
    const depositAddress = userWallet;
    const parsedAmount = parseFloat(amount);

    const res = await fetch(
      `${import.meta.env.VITE_SERVERURL}/create-coinbase-headless-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
        },
        body: JSON.stringify({
          amount: parsedAmount.toString(),
          walletAddress: depositAddress,
          email: data.email,
          phoneNumber: data.phoneNumber,
          country: data.country,
          paymentMethod: data.paymentMethod,
        }),
      },
    );

    if (!res.ok) {
      const errData = await res.json();
      // Handle region-forbidden error with fallback suggestion
      if (
        errData.code === "guest_region_forbidden" ||
        errData.error?.includes("region")
      ) {
        toast.error(
          "Coinbase Headless Onramp is not available in your region. Try the standard Coinbase payment method instead.",
        );
        setSelectedMethod("coinbase-onramp");
        throw new Error("Region not supported for Headless Onramp");
      }
      throw new Error(
        errData.error || "Failed to create Coinbase Headless Onramp order",
      );
    }

    const data_response = await res.json();
    if (!data_response.orderId) throw new Error("No order ID received");

    // The order is now persisted server-side — let the page refresh its
    // Coinbase order tracker so the new order appears immediately.
    if (onSuccess) onSuccess();

    // Return the order details so the modal can display them. `mode` tells
    // the modal whether to embed the link (Apple/Google Pay guest buttons)
    // or open it in a new tab (the hosted card checkout).
    return {
      orderId: data_response.orderId,
      paymentLink: data_response.paymentLink || "",
      mode: (data_response.mode as "embed" | "redirect") || "embed",
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    try {
      if (!userWallet || !amount) {
        throw new Error("Please enter amount and ensure wallet is connected.");
      }

      const parsedAmount = parseFloat(amount);
      const effMin = minAmount ?? 0.5;
      const effMax = maxAmount ?? 10000;
      if (
        isNaN(parsedAmount) ||
        parsedAmount < effMin ||
        parsedAmount > effMax
      ) {
        throw new Error(
          `Amount must be between $${effMin} and $${effMax.toLocaleString()}.`,
        );
      }

      if (!ethers.isAddress(userWallet)) {
        throw new Error("Invalid wallet address");
      }

      // For public collect pages and public invoice pages: create a payment
      // invoice, claim a dedicated wallet, then redirect to the invoice page.
      // The invoice page handles all payment-method-specific UI and status
      // polling. In-app flows skip this and process inline.
      if (usesInvoiceFlow) {
        if (requiresBuyerEmail && !buyerEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          throw new Error("Please enter a valid email address.");
        }
        const inv = await createPaymentInvoice({
          buyerEmail: buyerEmail.trim(),
          amount: parsedAmount,
          sellerWallet: targetWalletAddress,
          sellerUserId,
          sellerUsername: recipientName,
          source: invoiceSource!,
          paymentMethod: selectedMethod,
          collectSlug: invoiceCollectSlug,
        });
        // Append `?method=…` to the URL so the invoice page knows
        // which payment method to render even after a hard refresh or
        // when the link is shared — navigate-state alone is lost on
        // reload, and the Firestore `paymentMethod` field can lag (or
        // be a synonym the renderer doesn't recognise).
        navigate(`/payment-invoice/${inv.invoiceId}?method=${encodeURIComponent(selectedMethod)}`, {
          state: {
            method: selectedMethod,
            amount: parsedAmount,
            dedicatedAddress: inv.dedicatedWalletAddress,
            buyerEmail: buyerEmail.trim(),
            sellerName: recipientName,
            sellerPhoto: recipientPhotoURL,
            source: invoiceSource,
            sellerWallet: targetWalletAddress,
          },
        });
        return;
      }

      const depositAddress = userWallet;

      if (selectedMethod === "wallet") {
        setIsWalletModalOpen(true);
        return;
      }

      if (selectedMethod === "card-bank") {
        const res = await fetch(
          `${import.meta.env.VITE_SERVERURL}/create-onramp-session`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
            },
            body: JSON.stringify({
              transaction_details: {
                destination_currency: "usdc",
                destination_exchange_amount: parsedAmount.toString(),
                destination_amount: parsedAmount.toString(),
                destination_network: "base",
                destination_address: depositAddress,
                wallet_address: depositAddress,
              },
            }),
          },
        );

        if (!res.ok)
          throw new Error(
            "This payment method is currently unavailable for your region. Please try a different payment method or check the supported countries.",
          );

        const data = await res.json();
        if (!data.clientSecret)
          throw new Error(
            "Unable to initialize payment. Please try again later or use a different payment method.",
          );

        setStripeClientSecret(data.clientSecret);
        setIsStripeOnrampOpen(true);
        toast.success("Stripe OnRamp session ready!");
        return;
      }

      // Coinbase — the "Pay with Visa / MasterCard, Apple Pay or Google Pay"
      // method. It now runs on the Headless Onramp: selecting it opens the
      // embedded modal (Apple Pay / Google Pay inline, card via Coinbase's
      // hosted checkout) instead of a full-page redirect.
      if (
        selectedMethod === "coinbase-onramp" ||
        selectedMethod === "coinbase-headless"
      ) {
        // Open the Coinbase Headless modal — it handles all form validation and submission
        setIsCoinbaseHeadlessOpen(true);
        return;
      }

      if (selectedMethod === "mobile-wallets") {
        toast("This payment method is coming soon!", { icon: "⏳" });
        return;
      }

      // Both `lightning-btc` (Add Money via Lightning) and
      // `lightning-cashapp` (CashApp Pay) navigate to the same /lightning
      // page — they're sibling tiles backed by the same Lightning rail.
      // The difference: `lightning-btc` pins the provider to FixedFloat
      // unconditionally (no SimpleSwap fallback, error if FF is down) and
      // surfaces a "Powered by ff.io" badge on the invoice page. The
      // CashApp Pay tile keeps the default FF-primary-with-SimpleSwap-
      // fallback behaviour so small-amount edge cases still work even
      // when FF is temporarily degraded.
      if (selectedMethod === "lightning-btc" || selectedMethod === "lightning-cashapp") {
        navigate("/lightning", {
          state: {
            amount: parsedAmount,
            toAddress: depositAddress,
            invoiceId: activeInvoiceId || undefined,
            forceProvider:
              selectedMethod === "lightning-btc" ? "ff" : undefined,
            method: selectedMethod,
          },
        });
        return;
      }

      // PYUSD via PayPal / Venmo — single destination page, source query
      // param drives the branded copy. Both ids end up at the same
      // SimpleSwap exchange (PYUSD on Ethereum → USDC on Base).
      if (selectedMethod === "paypal" || selectedMethod === "venmo") {
        navigate(`/add-funds/pyusd?source=${selectedMethod}`, {
          state: {
            amount: parsedAmount,
            toAddress: depositAddress,
            source: selectedMethod,
            invoiceId: activeInvoiceId || undefined,
          },
        });
        return;
      }

      if (selectedMethod === "cashapp") {
        setLoading(false);
        setShowExtensionDisclaimer(true);
        proceedWithCashAppAfterDisclaimer(depositAddress);
        return;
      }

      // Fallback → Crossmint
      const crossmintRes = await fetch(
        `${import.meta.env.VITE_SERVERURL}/create-crossmint-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
          },
          body: JSON.stringify({
            recipient: depositAddress,
            amount: parsedAmount.toString(),
            currency: "usd",
            chain: "base",
            contractAddress: "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",
            email: resolvedEmail,
          }),
        },
      );

      if (!crossmintRes.ok) throw new Error("Failed to create Crossmint order");

      const crossData = await crossmintRes.json();
      if (crossData.orderId && crossData.clientSecret) {
        setCrossmintOrderId(crossData.orderId);
        setCrossmintClientSecret(crossData.clientSecret);
        setIsCrossmintModalOpen(true);
        toast.success("Crossmint checkout initialized!");
      } else {
        throw new Error("Invalid Crossmint response");
      }
    } catch (err: any) {
      console.error(err);
      const msg = err.message || "";
      // Show friendly messages we set, generic fallback for unexpected errors
      if (
        msg.includes("region") ||
        msg.includes("unavailable") ||
        msg.includes("payment method") ||
        msg.includes("try again")
      ) {
        toast.error(msg);
      } else {
        toast.error(
          "Something went wrong. Please try again or use a different payment method.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isStripeOnrampOpen || !stripeClientSecret) return;

    const loadStripeScripts = async () => {
      if (!document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
        const stripeScript = document.createElement("script");
        stripeScript.src = "https://js.stripe.com/v3/";
        stripeScript.async = true;
        document.body.appendChild(stripeScript);

        await new Promise((resolve, reject) => {
          stripeScript.onload = resolve;
          stripeScript.onerror = () =>
            reject(new Error("Failed to load Stripe.js"));
        });
      }

      if (
        !document.querySelector(
          'script[src="https://crypto-js.stripe.com/crypto-onramp-outer.js"]',
        )
      ) {
        const onrampScript = document.createElement("script");
        onrampScript.src =
          "https://crypto-js.stripe.com/crypto-onramp-outer.js";
        onrampScript.async = true;
        document.body.appendChild(onrampScript);

        await new Promise((resolve, reject) => {
          onrampScript.onload = resolve;
          onrampScript.onerror = () =>
            reject(new Error("Failed to load Stripe Onramp script"));
        });
      }

      const StripeOnramp = (window as any).StripeOnramp;
      if (StripeOnramp) {
        const stripeOnramp = new StripeOnramp(
          import.meta.env.VITE_PUBLIC_STRIPE_PUBLISHABLE_KEY,
        );
        const session = stripeOnramp.createSession({
          clientSecret: stripeClientSecret,
        });
        session.mount("#stripe-onramp-container");
      } else {
        console.error("StripeOnramp not available on window");
        toast.error("Failed to initialize Stripe OnRamp");
      }
    };

    loadStripeScripts().catch((err) => {
      console.error(err);
      toast.error("Failed to initialize Stripe OnRamp");
    });
  }, [isStripeOnrampOpen, stripeClientSecret]);

  const StripeOnrampModal = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-[1000] p-4">
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] rounded-3xl max-w-xl w-full max-h-[92vh] overflow-hidden border border-gray-700/50 flex flex-col">
        <div className="flex justify-between items-center p-5 sm:p-6 border-b border-gray-700/50">
          <h2 className="text-lg sm:text-xl font-bold text-white">
            Add Funds with Card/Bank
          </h2>
          <button
            onClick={() => {
              setIsStripeOnrampOpen(false);
              setStripeClientSecret("");
            }}
            className="text-red-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>
        <div className="flex-1 p-4 sm:p-6 overflow-auto">
          <div
            id="stripe-onramp-container"
            style={{ height: "100%", minHeight: "500px", width: "100%" }}
          />
        </div>
        <div className="px-5 py-4 border-t border-gray-700/50 bg-[#0F1419]/80">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-white mb-1">
                Secure Processing Notice
              </p>
              <p className="text-xs text-gray-400 leading-relaxed">
                Stripe is currently processing your order. All payment
                information is securely handled and protected by Stripe. CashTap
                does not have access to your payment details.
              </p>
              <p className="text-xs text-amber-400/80 mt-1.5">
                Note: Stripe may ask for identity verification (e.g. SSN) for US
                customers — this is handled entirely by Stripe for regulatory
                compliance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const CrossmintCheckoutModal = () => {
    if (!crossmintOrderId || !crossmintClientSecret) return null;
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-[1000] p-4">
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] rounded-3xl w-full max-w-lg max-h-[90vh] overflow-auto relative border border-gray-700/50">
          <div className="p-5 sm:p-6">
            <div className="flex justify-between items-center mb-5 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-white">
                Complete Payment
              </h2>
              <button
                onClick={() => {
                  setIsCrossmintModalOpen(false);
                  setCrossmintOrderId(null);
                  setCrossmintClientSecret(null);
                  if (userWallet)
                    getBalance(userWallet)
                      .then(setWalletBalance)
                      .catch(console.error);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            <div className="max-w-[450px] w-full mx-auto">
              <CrossmintEmbeddedCheckout
                orderId={crossmintOrderId}
                clientSecret={crossmintClientSecret}
                payment={{
                  receiptEmail: resolvedEmail,
                  crypto: { enabled: false },
                  fiat: { enabled: true },
                  defaultMethod: "fiat",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const WalletTransferModal = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md z-[1000] p-4">
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] rounded-3xl w-full max-w-md border border-gray-700/50 relative overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-gray-700/50">
          <div className="flex items-center gap-3">
            <QrCode className="w-6 h-6 text-[#61CE70]" />
            <h2 className="text-xl font-bold text-white">Deposit USDC</h2>
          </div>
          <button
            onClick={() => setIsWalletModalOpen(false)}
            className="text-gray-400 hover:text-white text-2xl transition-colors"
          >
            <X size={28} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="text-center">
            <p className="text-gray-300 mb-4">
              Send <strong>USDC</strong> on the <strong>Base</strong> network to
              this address:
            </p>

            {qrCodeUrl ? (
              <div className="flex flex-col items-center gap-5">
                <div className="bg-white p-4 rounded-2xl shadow-2xl">
                  <img
                    src={qrCodeUrl}
                    alt="Wallet QR Code"
                    className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
                  />
                </div>
                <div className="w-full bg-gray-900/60 rounded-xl p-4 border border-gray-700/50">
                  <div className="flex items-center justify-between gap-3">
                    <code className="text-[0.8rem] font-mono text-gray-200 break-all">
                      {activeDedicatedAddress || userWallet}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          activeDedicatedAddress || userWallet,
                        );
                        toast.success("Copied!");
                      }}
                      className="p-2 hover:bg-gray-800 rounded-lg transition-colors shrink-0"
                    >
                      <Copy className="w-5 h-5 text-gray-400 hover:text-[#61CE70]" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500 py-8">Generating QR code...</div>
            )}
          </div>

          <div className="text-center text-sm text-gray-400">
            Only send <strong>USDC on Base</strong>.<br />
            Other networks or tokens may be lost.
          </div>

          <button
            onClick={() => setIsWalletModalOpen(false)}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl font-medium transition-colors mt-4"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const ExtensionDisclaimerModal = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md z-[1100] p-4 sm:p-6">
      <div
        className="
          bg-gradient-to-br from-[#0F1419] to-[#1A1F2B]
          h-[90vh] overflow-y-auto scrollbar-hide
          rounded-3xl w-full max-w-2xl
          border border-gray-700/50
          relative shadow-2xl
        "
        style={{
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <div className="px-6 pt-8 pb-5 flex flex-col items-center border-b border-gray-800/60 sticky top-0 bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] z-10">
          <AlertTriangle
            size={68}
            className="text-yellow-500 mb-5"
            strokeWidth={1.6}
          />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center">
            Important Note
          </h2>
        </div>

        <div className="p-6 sm:p-8 space-y-8">
          <div className="bg-red-950/20 border border-red-900/40 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <ShieldAlert size={24} className="text-red-400" />
              <h3 className="text-lg font-semibold text-red-300">
                Why the popup might not appear
              </h3>
            </div>
            <ul className="space-y-3 text-gray-300 text-base">
              <li className="flex items-start gap-3">
                <Globe size={18} className="text-gray-500 mt-1 shrink-0" />
                <span>
                  Older browser version (Chrome/Edge should be up to date)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <RefreshCw size={18} className="text-gray-500 mt-1 shrink-0" />
                <span>
                  Browser popup blocker or strict privacy settings enabled
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Download size={18} className="text-gray-500 mt-1 shrink-0" />
                <span>Outdated or incorrectly installed Cashtap extension</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-950/20 border border-blue-900/40 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={24} className="text-blue-400" />
              <h3 className="text-lg font-semibold text-blue-300">
                How to fix it
              </h3>
            </div>
            <ol className="space-y-3 text-gray-300 text-base list-decimal list-inside">
              <li>Update your browser to the latest version</li>
              <li>Check browser popup settings and whitelist cashtap.cash</li>
              <li>Reinstall the Cashtap extension</li>
              <li>Clear browser cache and reload</li>
            </ol>
          </div>

          <p className="text-gray-400 text-sm italic">
            If you continue to experience issues, please contact our support
            team.
          </p>
        </div>

        <div className="px-6 sm:px-10 pb-8 pt-5 sticky bottom-0 bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] z-10 border-t border-gray-800/40">
          <button
            onClick={() => setShowExtensionDisclaimer(false)}
            className="w-full bg-[#61CE70] hover:bg-[#50B060] text-white py-3 rounded-xl font-medium transition-colors"
          >
            I Understand, Proceed
          </button>
        </div>
      </div>
    </div>
  );

  const PeerSuccessModal = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md z-[1000] p-4">
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] rounded-3xl w-full max-w-md border border-gray-700/50 relative overflow-hidden text-center p-8">
        <CheckCircle size={64} className="mx-auto text-[#61CE70] mb-6" />
        <h2 className="text-2xl font-bold text-white mb-4">Success!</h2>
        <p className="text-gray-300 mb-6">
          Your payment completed successfully.
          <br />
          USDC should appear in the wallet shortly.
        </p>
        <button
          onClick={() => {
            setIsPeerSuccessModalOpen(false);
            if (!isPublic) {
              navigate("/wallet");
            }
            onSuccess?.();
          }}
          className="w-full bg-[#61CE70] hover:bg-[#50B060] text-white py-3 rounded-xl font-medium transition-colors"
        >
          {isPublic ? "Done" : "Go to Wallet"}
        </button>
      </div>
    </div>
  );

  function truncateAddress(address: string): string {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  return (
    <div className={compact ? "w-full" : "w-full space-y-6 sm:space-y-8"}>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: isLight
            ? {
                background: "#fff",
                color: "#111",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                fontSize: "14px",
              }
            : {
                background: "#0F1419",
                color: "#fff",
                border: "1px solid #374151",
                borderRadius: "12px",
                fontSize: "14px",
              },
          success: { iconTheme: { primary: "#61CE70", secondary: "#fff" } },
          error: { iconTheme: { primary: "#EF4444", secondary: "#fff" } },
        }}
      />

      {isWalletModalOpen && <WalletTransferModal />}
      {isStripeOnrampOpen && <StripeOnrampModal />}
      {isCrossmintModalOpen && <CrossmintCheckoutModal />}
      <CoinbaseHeadlessOnrampModal
        isOpen={isCoinbaseHeadlessOpen}
        onClose={() => {
          setIsCoinbaseHeadlessOpen(false);
          setLoading(false);
        }}
        onSubmit={handleCoinbaseHeadlessSubmit}
        onCheckStatus={(orderId) =>
          refreshCoinbaseOrderStatus(orderId).then((r) => r?.status ?? null)
        }
        amount={parseFloat(amount) || 0}
        theme={theme}
      />
      {showExtensionDisclaimer && <ExtensionDisclaimerModal />}
      {isPeerSuccessModalOpen && <PeerSuccessModal />}

      {/* Recipient info for public pages */}
      {isPublic && recipientName && !compact && (
        <div className={`${cardBg} rounded-3xl p-5 sm:p-6 md:p-8`}>
          <div className="flex items-center gap-4">
            {recipientPhotoURL ? (
              <img
                src={recipientPhotoURL}
                alt={recipientName}
                className="w-14 h-14 rounded-full object-cover border-2 border-[#61CE70]/30"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#61CE70]/20 flex items-center justify-center">
                <Wallet className="w-7 h-7 text-[#61CE70]" />
              </div>
            )}
            <div>
              <p className={`${headingText} font-semibold text-lg`}>
                {recipientName}
              </p>
              <p className={`${bodyText} text-sm font-mono`}>
                {truncateAddress(userWallet)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Email input removed — not needed on public payment pages */}

      {/* Wallet Display */}
      {showWalletDisplay && !isPublic && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5 sm:p-6 md:p-8 backdrop-blur-sm">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4">
            Your Wallet
          </h3>
          <div className="p-4 bg-[#61CE70]/10 border border-[#61CE70]/30 rounded-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <div className="p-2 bg-[#61CE70]/20 rounded-lg shrink-0">
                  <Wallet className="w-5 h-5 text-[#61CE70]" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-medium">CashTap Wallet</p>
                  <p className="text-gray-400 text-xs sm:text-sm font-mono break-all">
                    {truncateAddress(userWallet)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(userWallet);
                  toast.success("Copied!");
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors shrink-0"
                type="button"
              >
                <Copy className="w-4 h-4 text-gray-400 hover:text-[#61CE70]" />
              </button>
            </div>
            <div className="mt-4 pt-4 border-t border-[#61CE70]/20">
              <div className="flex justify-between items-center">
                <span className="text-gray-300 text-sm">Current Balance:</span>
                <span className="text-[#61CE70] font-medium">
                  {loading ? "Loading..." : `${parseFloat(walletBalance)} USD`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={compact ? "space-y-2.5 sm:space-y-3" : "space-y-6"}
      >
        {/* Buyer email — required in public mode so the seller gets
            attribution and the buyer receives a receipt. Suppressed for
            sellers who have opted out via disabledEmailRecipients config. */}
        {/* Email + Amount — side by side on desktop in compact mode */}
        <div
          className={
            compact
              ? "flex flex-col sm:flex-row sm:items-start gap-2.5 sm:gap-3 sm:[&>*]:flex-1 sm:[&>*]:min-w-0"
              : "space-y-6"
          }
        >
        {requiresBuyerEmail && (
          <div className={sectionCard}>
            <label
              className={
                compact
                  ? `block ${sectionHeading}`
                  : `block text-xs uppercase tracking-wider font-semibold mb-2 ${mutedText}`
              }
            >
              Email Address
            </label>
            <input
              type="email"
              required
              value={buyerEmail}
              onChange={(e) => setBuyerEmail(e.target.value)}
              placeholder="you@example.com"
              className={`w-full px-4 border outline-none ${compact ? "py-2.5 text-base rounded-xl" : "py-3 text-sm rounded-2xl"} ${inputBg}`}
            />
            {compact ? (
              <p className={`hidden sm:block text-[11px] mt-1 ${mutedText}`}>
                Your receipt will be sent here.
              </p>
            ) : (
              <p className={`text-[11px] mt-2 ${mutedText}`}>
                We'll email you a receipt and notify {recipientName || "the recipient"} once your payment is confirmed.
              </p>
            )}
          </div>
        )}

        {/* Preset Amounts */}
        {presetAmounts && presetAmounts.length > 0 && (
          <div className={sectionCard}>
            <h3 className={sectionHeading}>
              Select Your Donation Amount
            </h3>
            <div className="flex flex-wrap gap-3">
              {presetAmounts.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmount(preset.toString())}
                  className={`px-6 py-3 rounded-2xl font-semibold text-lg transition-all ${
                    amount === preset.toString()
                      ? "bg-[#61CE70] text-white shadow-lg shadow-[#61CE70]/20"
                      : isLight
                        ? "bg-gray-100 text-gray-700 border border-gray-200 hover:border-[#61CE70]/50"
                        : "bg-gray-800/50 text-gray-300 border border-gray-700/50 hover:border-[#61CE70]/50"
                  }`}
                >
                  ${preset}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Amount Input */}
        <div className={sectionCard}>
          <h3 className={sectionHeading}>
            {fixedAmount ? "Amount Due" : "Amount (USD)"}
          </h3>
          {fixedAmount ? (
            <div
              className={`${isLight ? "bg-gray-50 border-gray-200" : "bg-gray-800/30 border-gray-700/50"} border rounded-2xl p-5 text-center`}
            >
              <p className={`text-3xl font-bold ${headingText}`}>
                ${fixedAmount.toFixed(2)}
              </p>
              <p className={`${bodyText} text-sm mt-1`}>USD</p>
            </div>
          ) : (
            <>
              <div className="relative">
                <span
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${bodyText} ${compact ? "text-base" : "text-lg"}`}
                >
                  $
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={`w-full pl-10 pr-4 border transition-all ${compact ? "py-2.5 text-base rounded-xl" : "py-4 text-lg rounded-2xl"} ${inputBg}`}
                  placeholder={amountPlaceholder || "0.00"}
                  min={minAmount ?? 0.5}
                  max={maxAmount ?? 10000}
                  step="0.01"
                  required
                />
              </div>
              <p className={`${bodyText} ${compact ? "text-[11px] mt-1" : "text-xs sm:text-sm mt-2"}`}>
                Minimum: ${minAmount ?? 0.5} | Maximum:{" "}
                {maxAmount ? `$${maxAmount.toLocaleString()}` : "$10,000"}
              </p>
            </>
          )}
        </div>
        </div>

        {/* Payment Methods */}
        <div className={sectionCard}>
          <h3 className={sectionHeading}>Select Payment Method</h3>
          <div className={compact ? "flex flex-col gap-2 sm:gap-2.5" : "flex flex-col gap-4"}>
            {paymentMethods
              .filter((m) => !disabledPaymentMethods.includes(m.id))
              // Globally-hidden methods (e.g. `cashapp` while the peer
              // extension is down) never render in any picker.
              .filter((m) => !HIDDEN_PAYMENT_METHOD_IDS.includes(m.id))
              // Stripe (card-bank) is only available on the user's own Add Funds page,
              // not on public collect pages — one wallet can't be registered with
              // multiple Stripe/Link identities.
              .filter((m) => !(isPublic && m.id === "card-bank"))
              .map((method) => (
                <button
                  key={method.id}
                  type="button"
                  disabled={
                    method.disabled || (method.mobileDisabled && isMobile)
                  }
                  onClick={() => {
                    if (method.disabled && method.comingSoon) {
                      toast("Coming soon!", { icon: "⏳" });
                    } else if (method.mobileDisabled && isMobile) {
                      toast("Please use a desktop browser to enable Cash App", {
                        icon: "💻",
                      });
                    } else {
                      setSelectedMethod(method.id);
                    }
                  }}
                  className={`
                  w-full ${compact ? "p-2.5 sm:px-4 sm:py-2.5" : "p-4 sm:p-5"} rounded-2xl border transition-all duration-200
                  flex flex-col text-left relative
                  ${
                    method.disabled || (method.mobileDisabled && isMobile)
                      ? isLight
                        ? "opacity-50 cursor-not-allowed bg-gray-50 border-orange-200/50"
                        : "opacity-50 cursor-not-allowed bg-gray-900/30 border-orange-200/30"
                      : selectedMethod === method.id
                        ? "border-[#61CE70] bg-[#61CE70]/10 shadow-lg shadow-[#61CE70]/20"
                        : isLight
                          ? "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100"
                          : "border-gray-700/50 bg-gray-800/20 hover:border-gray-600 hover:bg-gray-800/30"
                  }
                `}
                >
                  <div className="flex items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">{method.icon}</div>

                    {(method.comingSoon ||
                      (method.mobileDisabled && isMobile)) && (
                      <span className="text-[8px] absolute top-2 md:top-5 right-2 md:right-5 sm:text-xs border border-yellow-500/70 bg-yellow-900/30 px-1 md:px-2.5 py-1 rounded-lg font-semibold uppercase tracking-wide text-yellow-300/90 whitespace-nowrap">
                        {method.mobileDisabled && isMobile
                          ? "Desktop Only"
                          : "Coming Soon"}
                      </span>
                    )}
                  </div>
                </button>
              ))}
          </div>
        </div>

        {/* Order Summary — shown when amount + method selected */}
        {amount && parseFloat(amount) > 0 && selectedMethod && (
          <div
            className={
              compact
                ? `rounded-2xl p-3 border ${dividerBorder} ${isLight ? "bg-gray-50" : "bg-gray-800/20"}`
                : `${cardBg} rounded-3xl p-5 sm:p-6 md:p-8`
            }
          >
            <h3 className={sectionHeading}>Order Summary</h3>
            <div className="space-y-3">
              {recipientName && (
                <div className="flex items-center justify-between text-sm sm:text-base">
                  <span className={bodyText}>To</span>
                  <div className="flex items-center gap-2">
                    {recipientPhotoURL && (
                      <img
                        src={recipientPhotoURL}
                        alt={recipientName}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    )}
                    <span className={`${headingText} font-medium`}>
                      {recipientName}
                    </span>
                  </div>
                </div>
              )}
              <div className="flex justify-between text-sm sm:text-base">
                <span className={bodyText}>Amount</span>
                <span className={headingText}>
                  ${parseFloat(amount).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm sm:text-base">
                <span className={bodyText}>Payment Method</span>
                <span className={headingText}>
                  {paymentMethods.find((m) => m.id === selectedMethod)?.name ||
                    selectedMethod}
                </span>
              </div>

              <div className="flex justify-between text-sm sm:text-base">
                <span className={bodyText}>Processing Fee</span>
                <span className={headingText}>$0.00</span>
              </div>
              <div className={`border-t ${dividerBorder} pt-3`}>
                <div className="flex justify-between">
                  <span className={`${headingText} font-medium`}>Total</span>
                  <span
                    className="font-bold text-base sm:text-lg"
                    style={{ color: accentColor || "#61CE70" }}
                  >
                    ${parseFloat(amount).toFixed(2)} USD
                  </span>
                </div>
              </div>
            </div>

            {/* Security & About — full checklist only outside compact mode */}
            {!compact && (
              <div className={`mt-6 pt-5 border-t ${dividerBorder}`}>
                <div className="space-y-3 text-sm sm:text-base">
                  <div className="flex items-center space-x-3">
                    <Shield className="w-5 h-5 text-[#61CE70] shrink-0" />
                    <span className={isLight ? "text-gray-600" : "text-gray-300"}>
                      256-bit SSL encryption
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-[#61CE70] shrink-0" />
                    <span className={isLight ? "text-gray-600" : "text-gray-300"}>
                      Instant settlement on Base network
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#61CE70] shrink-0" />
                    <span className={isLight ? "text-gray-600" : "text-gray-300"}>
                      KYC/AML compliant
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Powered by CashTap */}
            {!compact && (
              <div
                className={`flex justify-center items-center mt-2 pt-4 border-t ${dividerBorder} gap-3`}
              >
                <div
                  className={`text-xs mt-5 scale-100 md:scale-125 flex justify-center items-center ${mutedText} leading-tight`}
                >
                  <span className="block">
                    Powered by{" "}
                    <span className="text-[#61CE70] font-semibold">CashTap</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Disclaimer — shown only on public collect pages */}
        {isPublic && (
          <label
            className={`flex items-start gap-3 cursor-pointer rounded-2xl border transition-colors duration-150 ${compact ? "p-2.5" : "p-4 mt-2"}
            ${
              disclaimerAgreed
                ? "border-[#61CE70]/30 bg-[#61CE70]/5"
                : isLight
                  ? "border-gray-200 bg-gray-50"
                  : "border-gray-700/50 bg-gray-800/20"
            }`}
          >
            <div className="relative shrink-0 mt-0.5">
              <input
                type="checkbox"
                className="sr-only"
                checked={disclaimerAgreed}
                onChange={(e) => setDisclaimerAgreed(e.target.checked)}
              />
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-150
                ${
                  disclaimerAgreed
                    ? "bg-[#61CE70] border-[#61CE70]"
                    : isLight
                      ? "border-gray-300 bg-white"
                      : "border-gray-600 bg-gray-800"
                }`}
              >
                {disclaimerAgreed && (
                  <svg
                    className="w-3 h-3 text-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </div>
            <p
              className={`text-xs leading-relaxed select-none ${isLight ? "text-gray-600" : "text-gray-400"}`}
            >
              By continuing, you confirm this transfer is authorized by you and
              is final.{" "}
              <span
                className={
                  isLight
                    ? "text-gray-800 font-medium"
                    : "text-gray-300 font-medium"
                }
              >
                Transfers are irreversible.
              </span>{" "}
              <span className={compact ? "hidden sm:inline" : ""}>
                CashTap does not hold, cancel, or reverse completed transfers.
                Beware of scams.
              </span>
            </p>
          </label>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={
            !selectedMethod ||
            !amount ||
            !userWallet ||
            loading ||
            peerProcessing ||
            (isPublic && !disclaimerAgreed) ||
            paymentMethods.find((m) => m.id === selectedMethod)?.disabled
          }
          className={`w-full text-white rounded-2xl font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${compact ? "py-3 mt-0.5 sm:mt-1" : "py-4 mt-6"}`}
          style={{
            background: accentColor
              ? `linear-gradient(to right, ${accentColor}, ${accentColor}dd)`
              : "linear-gradient(to right, #61CE70, #50B060)",
            boxShadow: accentColor
              ? `0 10px 15px -3px ${accentColor}33`
              : undefined,
          }}
        >
          {loading || peerProcessing ? "Processing..." : buttonText}
        </button>
      </form>

      {/* Manage Payment Methods */}
      {showManagePayments && !isPublic && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5 sm:p-6 md:p-8 backdrop-blur-sm">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4">
            Manage Payment Methods
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/setup-usbank")}
              type="button"
              className="p-4 sm:p-5 md:p-6 rounded-2xl border border-gray-700/50 bg-gray-800/30 hover:border-gray-600/50 transition-all duration-200 flex items-center space-x-3"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white bg-blue-600 shrink-0">
                <CreditCard size={20} />
              </div>
              <span className="font-medium text-white">Link Bank Account</span>
            </button>

            <button
              type="button"
              disabled
              className="p-4 sm:p-5 md:p-6 rounded-2xl border border-gray-800/50 bg-gray-900/30 opacity-50 cursor-not-allowed flex items-center space-x-3"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white bg-gray-600 shrink-0">
                <CreditCard size={20} />
              </div>
              <div>
                <span className="text-gray-400 font-medium">Add Card</span>
                <span className="text-xs text-gray-500 block">Coming soon</span>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* About CashTap — hidden in compact mode to keep the page short */}
      {!compact && (
      <div className={`${cardBg} rounded-3xl p-5 sm:p-6 md:p-8`}>
        <div className="flex items-center gap-3 mb-4">
          <img
            src="/cashtap-logo.png"
            alt="CashTap"
            className="h-8 w-auto"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <h3 className={`text-lg sm:text-xl font-bold ${headingText}`}>
            About CashTap
          </h3>
        </div>
        <p className={`${bodyText} text-sm sm:text-base leading-relaxed mb-4`}>
          CashTap enables fast, secure, and low-cost payments for sending and
          receiving money globally. Enjoy instant settlements, no hidden fees,
          and full transparency on every transaction. Built for simplicity,
          CashTap makes modern digital payments easy and reliable.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div
            className={`${isLight ? "bg-white border border-gray-100 shadow-sm" : "bg-gray-800/30"} rounded-xl p-3 text-center`}
          >
            <Shield className="w-5 h-5 text-[#61CE70] mx-auto mb-1.5" />
            <p className={`${headingText} text-xs font-medium`}>Secure</p>
            <p className={`${mutedText} text-[10px]`}>End-to-end encrypted</p>
          </div>
          <div
            className={`${isLight ? "bg-white border border-gray-100 shadow-sm" : "bg-gray-800/30"} rounded-xl p-3 text-center`}
          >
            <Clock className="w-5 h-5 text-[#61CE70] mx-auto mb-1.5" />
            <p className={`${headingText} text-xs font-medium`}>Instant</p>
            <p className={`${mutedText} text-[10px]`}>Real-time settlement</p>
          </div>
          <div
            className={`${isLight ? "bg-white border border-gray-100 shadow-sm" : "bg-gray-800/30"} rounded-xl p-3 text-center`}
          >
            <CheckCircle className="w-5 h-5 text-[#61CE70] mx-auto mb-1.5" />
            <p className={`${headingText} text-xs font-medium`}>Compliant</p>
            <p className={`${mutedText} text-[10px]`}>KYC/AML verified</p>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default PaymentFlow;
